/**
 * Filters out empty or invalid parts from message content.
 *
 * Removes:
 * - null/undefined parts
 * - Empty objects {}
 * - Non-object parts
 * - Metadata-only parts (parts with no content fields)
 * - Text parts with empty/whitespace content
 *
 * This prevents invalid data from being sent to AI models or stored.
 *
 * Used in pipelines:
 * - client-to-model: Clean user input
 * - firestore-to-model: Clean loaded history
 * - model-to-client: Clean parts before sending to client
 * - model-to-firestore: Clean parts before storage
 */

import type { CoreLogger } from '@elara/core/logger';

import type { MessagePart } from '#schemas/message-schemas.js';
import type { ProcessorContext } from '../types.js';
import type { PartResult } from './PartProcessor.js';

import { isTextPart } from '#schemas/message-schemas.js';

import { PartProcessor } from './PartProcessor.js';

// Content fields that indicate a part has actual content
const CONTENT_FIELDS = [
  'text',
  'media',
  'data',
  'reasoning',
  'resource',
  'toolRequest',
  'toolResponse',
  'widget',
  'custom',
];

export class FilterEmptyPartsProcessor extends PartProcessor {
  readonly name = 'FilterEmptyParts';

  constructor(
    baseLogger: CoreLogger,
    readonly dropEmptyModelParts: boolean = true,
  ) {
    super(baseLogger);
  }

  process(part: MessagePart, context: ProcessorContext): PartResult {
    if (!part) return { parts: [] };
    if (typeof part !== 'object') return { parts: [] };
    if (Object.keys(part).length === 0) return { parts: [] };

    // Filter metadata-only parts (parts with no content fields)
    // A part like { metadata: { foo: 'bar' } } is useless to the client
    const hasContentField = CONTENT_FIELDS.some((field) => field in part);
    if (!hasContentField) return { parts: [] };

    // Filter text parts with empty or whitespace-only text
    // Genkit rejects empty text parts
    if (isTextPart(part) && !part.text.trim()) {
      if (!this.dropEmptyModelParts && context.message?.role === 'model') {
        return { parts: [{ text: '🤷' }] };
      }
      return { parts: [] };
    }

    return { parts: [part] };
  }
}
